import logger from './logger';

/**
 * Utilities for filtering room invites based on Incoming Invites settings
 * 
 * This implements client-side invite filtering to block unwanted invites.
 * Does NOT use m.ignored_user_list (which blocks all events, not just invites).
 */

/**
 * Extract the domain from a Matrix user ID
 * @param {string} userId - Matrix user ID (e.g., @user:example.com)
 * @returns {string|null} Domain (e.g., example.com) or null if invalid
 */
export const extractDomain = (userId) => {
  if (!userId || !userId.startsWith('@')) {
    return null;
  }

  const colonIndex = userId.indexOf(':');
  if (colonIndex === -1) {
    return null;
  }

  return userId.substring(colonIndex + 1);
};

/**
 * Check if a user ID matches a pattern (supports exact match or domain match)
 * @param {string} userId - Matrix user ID to check (e.g., @user:example.com)
 * @param {string} pattern - Pattern to match. Can be:
 *   - Full Matrix ID starting with @: Must match exactly (e.g., @userA:matrix.org)
 *   - Domain without @: Matches against the domain part (e.g., matrix.org matches @userA:matrix.org)
 * @returns {boolean} True if matches
 */
export const matchesPattern = (userId, pattern) => {
  if (!userId || !pattern) {
    return false;
  }

  // If pattern starts with @, require exact full Matrix ID match
  if (pattern.startsWith('@')) {
    return userId.toLowerCase() === pattern.toLowerCase();
  }

  // Otherwise, treat pattern as a domain and match against the domain part
  const userDomain = extractDomain(userId);
  return userDomain.toLowerCase() === pattern.toLowerCase();
};

/**
 * Check if a user/domain is in a list. Group entries are skipped here explicitly —
 * they are evaluated separately by `matchGroups` because they require an async
 * classification lookup via the Messenger-Proxy isInsurance endpoint.
 * @param {string} userId - Matrix user ID to check
 * @param {Array} list - List of blocked/allowed items {value, type}
 * @returns {boolean} True if user or their domain is in the list
 */
export const isInList = (userId, list) => {
  if (!userId || !Array.isArray(list) || list.length === 0) {
    return false;
  }

  return list.some(item => {
    if (!item || !item.value || !item.type) return false;
    // Group entries are evaluated by matchGroups; skip here so that a group name
    // can never coincidentally collide with MXID/domain pattern matching.
    if (item.type === 'group') return false;
    return matchesPattern(userId, item.value);
  });
};

/**
 * Evaluate group entries for an inviter using a caller-supplied resolver.
 * @param {string} inviterUserId
 * @param {Array} list - Full exception list (users/domains/groups mixed)
 * @param {Function} groupResolver - (inviterUserId, groupName) => true | false | null
 * @returns {true|false|null} true = a group matched; false = no group matched;
 *                            null = at least one group evaluation was ambiguous (no definitive match found)
 */
export const matchGroups = (inviterUserId, list, groupResolver) => {
  if (!Array.isArray(list) || list.length === 0 || typeof groupResolver !== 'function') {
    return false;
  }
  const groupEntries = list.filter(i => i && i.type === 'group' && i.value);
  if (groupEntries.length === 0) return false;

  let sawAmbiguous = false;
  for (const g of groupEntries) {
    const r = groupResolver(inviterUserId, g.value);
    if (r === true) return true;
    if (r === null || r === undefined) sawAmbiguous = true;
  }
  return sawAmbiguous ? null : false;
};

/**
 * Determine if an invite should be blocked based on Incoming Invites settings.
 *
 * Tri-state return when a groupResolver is provided:
 *   true  - block the invite
 *   false - allow the invite
 *   null  - ambiguous (caller should apply fail-mode). Only possible when a group
 *           entry exists and the resolver returned null for the classification.
 *
 * When no groupResolver is provided (legacy sync callers), the function stays
 * purely synchronous and treats group entries as "no match" — equivalent to the
 * prior behaviour, plus callers can still short-circuit on user/domain matches.
 *
 * @param {string} inviterUserId
 * @param {Object} settings - Incoming Invites settings
 * @param {Object} [opts]
 * @param {Function} [opts.groupResolver] - (inviterUserId, groupName) => true|false|null
 * @returns {boolean|null}
 */
export const shouldBlockInvite = (
  inviterUserId,
  settings,
  opts = {},
) => {
  if (!inviterUserId || !settings) {
    logger.warn('[inviteFiltering] Invalid parameters for shouldBlockInvite');
    return false;
  }

  const { groupResolver } = opts;
  const {
    firstContactDefaultPermission = 'allow-all',
    firstContactBlockedList = [],
    firstContactAllowedList = [],
  } = settings;

  const activeList = firstContactDefaultPermission === 'allow-all'
    ? firstContactBlockedList
    : firstContactAllowedList;

  // 1. User/domain match short-circuits on both modes.
  if (isInList(inviterUserId, activeList)) {
    if (firstContactDefaultPermission === 'allow-all') {
      logger.info(`[inviteFiltering] Blocking invite from ${inviterUserId} - in blocked list`);
      return true;
    }
    return false; // block-all mode: explicit allow
  }

  // 2. Group evaluation (only runs if a resolver is provided and there are group entries).
  const groupResult = matchGroups(inviterUserId, activeList, groupResolver);
  // AllowAll: group match => block. BlockAll: group match => allow.
  if (groupResult === true) {
    if (firstContactDefaultPermission === 'allow-all') {
      logger.info(`[inviteFiltering] Blocking invite from ${inviterUserId} - group exception matched (allow-all)`);
      return true;
    }
    logger.info(`[inviteFiltering] Allowing invite from ${inviterUserId} - group exception matched (block-all)`);
    return false;
  }
  if (groupResult === null) {
    // Ambiguous; let caller apply fail-mode.
    return null;
  }

  // 3. No match — fall back to mode default.
  if (firstContactDefaultPermission === 'allow-all') {
    return false;
  }
  logger.info(`[inviteFiltering] Blocking invite from ${inviterUserId} - not in allowed list (block-all mode)`);
  return true;
};
